import { reactive } from 'vue';

export type ToastColor =
  'bg-info' | 'bg-success' | 'bg-warning' | 'bg-danger' | 'bg-primary' | 'bg-secondary';
export interface Toast {
  id: number;
  message: string;
  color: ToastColor;
  loading: boolean;
}

/**
 * Toast-service (port van eportal ToastService/ToastStack), maar reactive i.p.v. via component-ref.
 * Gebruik: toastService.success('Opgeslagen'); const id = toastService.loading('Zoeken…'); toastService.finish(id, 'Klaar', 'bg-success')
 */
class ToastService {
  public readonly toasts = reactive<Toast[]>([]);
  private nextId = 1;
  private timers = new Map<number, ReturnType<typeof setTimeout>>();

  show(message: string, color: ToastColor = 'bg-info', autoHide = true, duration = 4000): number {
    const id = this.nextId++;
    this.toasts.push({ id, message, color, loading: false });
    if (autoHide)
      this.timers.set(
        id,
        setTimeout(() => this.remove(id), duration),
      );
    return id;
  }
  success(message: string, duration = 3500) {
    return this.show(message, 'bg-success', true, duration);
  }
  info(message: string, duration = 4000) {
    return this.show(message, 'bg-info', true, duration);
  }
  warning(message: string, duration = 6000) {
    return this.show(message, 'bg-warning', true, duration);
  }
  error(message: string, duration = 8000) {
    return this.show(message, 'bg-danger', true, duration);
  }
  /** Toast met spinner die blijft staan tot `finish`/`remove`. */
  loading(message: string, color: ToastColor = 'bg-info'): number {
    const id = this.nextId++;
    this.toasts.push({ id, message, color, loading: true });
    return id;
  }
  update(id: number, patch: Partial<Omit<Toast, 'id'>>) {
    const t = this.toasts.find((x) => x.id === id);
    if (t) Object.assign(t, patch);
  }
  finish(id: number, message?: string, color: ToastColor = 'bg-success', autoHide = true, duration = 4000) {
    if (!message) return this.remove(id);
    this.update(id, { message, color, loading: false });
    if (autoHide)
      this.timers.set(
        id,
        setTimeout(() => this.remove(id), duration),
      );
  }
  remove(id: number) {
    const i = this.toasts.findIndex((x) => x.id === id);
    if (i !== -1) this.toasts.splice(i, 1);
    const t = this.timers.get(id);
    if (t) clearTimeout(t);
    this.timers.delete(id);
  }
}
export const toastService = new ToastService();
